import { useEffect, useRef } from "react";

/**
 * Fires `onExpire` exactly when the quote expires — WITHOUT per-second state updates. The visual countdown
 * (which does need ticking) lives inside the small `QuoteRateBar`/`QuoteSummaryCard` components via
 * `useQuoteCountdown`; this hook is for hosts (the wizard page) that only care about the expiry EVENT, so
 * they don't re-render 60×/min while a quote is active.
 *
 * Behavior parity with `useQuoteCountdown`'s callback: fires immediately when `expiresAt` is already in the
 * past on mount/change, and re-arms whenever `expiresAt` changes (quote recalculated).
 *
 * @param clockOffsetMs Milliseconds to add to the browser's clock to read the SERVER's. Pass it wherever the
 *   submit guard is evaluated against a corrected clock, or the two disagree: a browser 45 s behind arms this
 *   timer 45 s late, and for those 45 s the button stays enabled over a quote the backend has already let
 *   lapse. Defaults to 0, which is the no-correction-available case.
 */
export function useQuoteExpiry(
  expiresAt: string | null | undefined,
  onExpire: () => void,
  clockOffsetMs = 0,
): void {
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    if (!expiresAt) return;
    const remaining = Date.parse(expiresAt) - (Date.now() + clockOffsetMs);
    // An unparseable timestamp yields NaN, and `NaN <= 0` is false — so without this guard the timeout is
    // scheduled with NaN, coerced to 0, and fires immediately for a quote that has not expired.
    if (!Number.isFinite(remaining)) return;
    if (remaining <= 0) {
      onExpireRef.current();
      return;
    }
    const id = window.setTimeout(() => onExpireRef.current(), remaining);
    return () => window.clearTimeout(id);
  }, [expiresAt, clockOffsetMs]);
}
